import { QuoteApi } from "../api/QuoteApi";
import { CalculationType, QuotingType } from "../state/quote-reducer";
import { BaseQuoteHandler } from "./BaseQuoteHandler";
import { IQuoteHandler } from "./interfaces/IQuoteHandler";
import { FixAndFlipQuoteDetail } from "./models/quote.model";
import { FixAndFlipCalculation } from "./models/short-term/short-term-calculation.model";
import { FixAndFlipQuote } from "./models/short-term/short-term.model";
import { MetricType } from "./types/MetricType";
import { QuoteCalculationStatus } from "./types/QuoteCalculationStatus";
import { QuoteLoadPayload } from "./types/QuoteLoadPayloadType";

export class ShortTermQuoteHandler
  extends BaseQuoteHandler<FixAndFlipQuoteDetail>
  implements IQuoteHandler
{
  public async load(id: number): Promise<QuoteLoadPayload> {
    const result = await super.loadBase(id);
    const { response } = result;
    return {
      id: id,
      quote: response.in,
      calculation: response.out,
      warnings: this.getWarningsFromCalculation(response.out),
      metadata: super.getMetadataFromQuote(response.metadata),
    };
  }

  public async calculate(
    quote: FixAndFlipQuote,
    id: number | null
  ): Promise<Partial<QuoteLoadPayload>> {
    quote.quoteId = id;
    const calculation = await super.calculateBase(quote as FixAndFlipQuote);

    calculation.out.BorrowerInfo.borrowerTier =
      calculation.out.BorrowerInfo.borrowerTier.toString();
    if (id) {
      await this.saveMetric(+id, "CALC", quote, calculation.out);
    }
    return {
      quote: quote,
      calculation: calculation.out,
      warnings: this.getWarningsFromCalculation(calculation.out),
    };
  }

  public async save(
    quote: QuotingType,
    calculation: CalculationType
  ): Promise<Partial<QuoteLoadPayload>> {
    const quoteToSave = this.formatQuoteForAction(quote, calculation);
    const newQuoteId = await super.saveBase(quoteToSave);

    await this.saveMetric(
      +newQuoteId,
      "SAVED",
      quoteToSave.in,
      quoteToSave.out
    );
    return {
      id: newQuoteId,
      quote: quoteToSave.in,
      calculation: quoteToSave.out,
      warnings: this.getWarningsFromCalculation(quoteToSave.out),
    };
  }

  public async submit(
    id: number,
    quote: QuotingType,
    calculation: CalculationType
  ): Promise<boolean> {
    const quoteToSubmit = this.formatQuoteForAction(quote, calculation);
    quoteToSubmit.out.id = id.toString();

    const hasQuoteBeenSubmitted = await super.submitBase(quoteToSubmit);

    if (hasQuoteBeenSubmitted) {
      await this.saveMetric(
        +id,
        "SUBMITTED",
        quoteToSubmit.in,
        quoteToSubmit.out
      );
    }

    return hasQuoteBeenSubmitted;
  }

  public async submitForReview(
    id: number,
    quote: QuotingType,
    calculation: CalculationType
  ) {
    const quoteToSubmit = this.formatQuoteForAction(quote, calculation);
    quoteToSubmit.out.id = id.toString();

    const hasQuoteBeenSubmitted = await super.submitForReviewBase(
      quoteToSubmit
    );

    if (hasQuoteBeenSubmitted) {
      await this.saveMetric(
        +id,
        "SUBMITTED",
        quoteToSubmit.in,
        quoteToSubmit.out
      );
    }

    return hasQuoteBeenSubmitted;
  }

  public async submitException(id: number, status: string | undefined) {
    const hasExceptionSubmitted = await super.submitExceptionBase(id, status);
    return hasExceptionSubmitted;
  }

  public async copy(
    quote: QuotingType,
    calculation: CalculationType
  ): Promise<Partial<QuoteLoadPayload>> {
    const quoteToCopy = this.formatQuoteForAction(quote, calculation, true);
    const newQuoteId = await super.copyBase(quoteToCopy);

    await this.saveMetric(
      +newQuoteId,
      "SAVED",
      quoteToCopy.in,
      quoteToCopy.out
    );

    return {
      id: newQuoteId,
      quote: quoteToCopy.in,
      calculation: quoteToCopy.out,
      warnings: this.getWarningsFromCalculation(quoteToCopy.out),
    };
  }

  public async update(
    id: number,
    quote: QuotingType,
    calculation: CalculationType
  ): Promise<Partial<QuoteLoadPayload>> {
    const quoteToUpdate = this.formatQuoteForAction(quote, calculation);
    quoteToUpdate.out.id = id.toString();
    const updatedQuoteId = await super.updateBase(id, quoteToUpdate);

    await this.saveMetric(
      +updatedQuoteId.out.id,
      "UPDATED",
      quoteToUpdate.in,
      quoteToUpdate.out
    );

    return {
      id: +updatedQuoteId.out.id,
      quote: quoteToUpdate.in,
      calculation: quoteToUpdate.out,
      warnings: this.getWarningsFromCalculation(quoteToUpdate.out),
    };
  }

  private formatQuoteForAction(
    quote: QuotingType,
    calculation: CalculationType,
    isCopy: boolean = false
  ): FixAndFlipQuoteDetail {
    if (isCopy && quote && calculation) {
      calculation.id = "";
      quote.sfaccountid = undefined;
      quote.sfdealid = undefined;
      quote.campaignId = undefined;
      quote.campaignName = undefined;
      quote.campaignExpirationDate = undefined;
      quote.campaignIncentive = undefined;
      quote.campaignCode = undefined;
      quote.hasSubmittedQuote = false;
      quote.exceptionPricingOverride = {};
      quote.exceptionPricingOverride.achRequired = "Yes";
      quote.exceptionPricingOverride.achExempt = "Non-exempt";
      if ("dealTerms" in quote) {
        quote as FixAndFlipQuote;
        quote.dealTerms && (quote.dealTerms.submittedDate = undefined);
      }
    }

    return new FixAndFlipQuoteDetail(
      quote as FixAndFlipQuote,
      calculation as FixAndFlipCalculation
    );
  }

  public async saveMetric(
    id: number | null,
    metric: MetricType,
    quote: FixAndFlipQuote,
    calculation: FixAndFlipCalculation
  ) {
    if (!id) return;

    const metricOptions = {
      env: process.env.REACT_APP_ENVIRONMENT ?? "DEV",
      producttype: quote.quoteType,
      quoteproducttype: quote.loanSummary?.loanProduct,
      quotetype: metric,
      username: quote.username ?? "",
      quoteid: +id,
      quoteloanamount: calculation.LTCCalcOnPurchaseRehab.loanAmt.toString(),
    };

    await QuoteApi.saveMetric(metricOptions);
  }

  private getWarningsFromCalculation(
    calc: FixAndFlipCalculation
  ): QuoteCalculationStatus {
    const calculation = calc?.validationErrors;
    const rateLLPAsFactored = calc.rateLLPAsFactored;
    const feeLLPAsFactored = calc.feeLLPAsFactored;
    const warnings = calc.warnings;
    const errors = calc.validationErrors;
    const overrides = calc.overridePricing;
    const calcStatus: QuoteCalculationStatus = {};

    calculation && (calcStatus.calculation = calculation);
    rateLLPAsFactored && (calcStatus.rateLLPAsFactored = rateLLPAsFactored);
    feeLLPAsFactored && (calcStatus.feeLLPAsFactored = feeLLPAsFactored);
    warnings && (calcStatus.warnings = warnings);
    errors && (calcStatus.errors = errors);
    overrides && (calcStatus.overridePricing = overrides);

    return calcStatus;
  }
}
